import "src/js/util/map_loader";

import * as turf from "@turf/turf";

import {
  Button,
  WithSkeletonLoadingProps,
  withSkeletonLoading,
} from "@landtechnologies/components";
import { Map } from "./renderers/Map";
import { Stores, useGlobalState } from "react-migration/lib/persistence/state";
import {
  downloadCanvasAsImage,
  generateBrandedCanvasFromImageUrl,
} from "react-migration/lib/util/mapHelpers";
import { AnchorHTMLAttributes, FC, useCallback, useEffect, useMemo } from "react";

import SITE_IMAGE from "react-migration/domains/sites/apollo/li-api/queries/siteImage.gql";
import SITE_OVERVIEW_US from "react-migration/domains/sites/apollo/li-api/queries/siteOverviewUS.gql";
import { SiteOverviewResponseData } from "../../typings";
import { geoJsonPolyToGoogleBounds } from "src/js/util/map_util";
import logSiteCardEvent from "src/js/view/sites/logSiteCardEvent";
import { routedClient } from "react-migration/lib/persistence/apollo";
import { useLazyQuery } from "@apollo/client/react/hooks/useLazyQuery";
import { useQuery } from "@apollo/client/react/hooks/useQuery";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { Layer } from "@deck.gl/core";
import { getMapSiteSelectionLocation } from "react-migration/lib/util";
import { Link } from "react-migration/components/Link";

type MapContainerProps = {
  siteId?: string;
  interactive?: boolean;
  showExportImageButton?: boolean;
  initialMapTypeId?: string;
  mapZoom?: number;
  onMapLoaded?: (isLoaded: boolean) => void;
  layers?: Layer<any>[];
};

export const MapContainer = withSkeletonLoading(
  ({
    hideSkeletonLoading,
    siteId,
    initialMapTypeId,
    mapZoom,
    interactive = true,
    showExportImageButton = false,
    onMapLoaded,
    layers,
  }: WithSkeletonLoadingProps & MapContainerProps) => {
    const { t } = useTranslation();
    const [{ siteCardId }] = useGlobalState(Stores.site);
    const siteIdFromPropsOrStore = siteId ?? siteCardId;

    const { loading, data } = useQuery<SiteOverviewResponseData>(SITE_OVERVIEW_US, {
      variables: { _id: siteIdFromPropsOrStore },
      client: routedClient,
    });

    const bounds = useMemo(() => {
      if (!loading && data) {
        return geoJsonPolyToGoogleBounds(data.siteOverview.geometry as GeoJSON.Polygon);
      }
    }, [loading, data]);

    const siteLatLng = useMemo(() => {
      if (!loading && data?.siteOverview.location) {
        return {
          lat: data.siteOverview.location.coordinates[1],
          lng: data.siteOverview.location.coordinates[0],
        };
      }
    }, [loading, data?.siteOverview.location]);

    const siteSelectionRouterLocation = useMemo(() => {
      if (!siteLatLng || !siteIdFromPropsOrStore || !data?.siteOverview.geometry) return null;

      return getMapSiteSelectionLocation({
        siteLatLng,
        geometry: data?.siteOverview.geometry,
        siteId: siteIdFromPropsOrStore,
      });
    }, [data?.siteOverview.geometry, siteIdFromPropsOrStore, siteLatLng]);

    const logViewOnMapClick = useCallback(
      () =>
        logSiteCardEvent({
          name: "Site Card - View Site In Map Mode Clicked",
          context: { source: "sitecard-lite" },
        }),
      []
    );

    const [loadSiteImage, { loading: loadingSiteImage }] = useLazyQuery(SITE_IMAGE, {
      variables: {
        siteId: siteIdFromPropsOrStore,
        mapZoom: mapZoom ?? 19,
      },
      client: routedClient,
      onCompleted: async ({ siteImage }) => {
        const boundaryImage = siteImage.boundary_image;
        const canvas = await generateBrandedCanvasFromImageUrl(boundaryImage.uri);

        downloadCanvasAsImage(canvas, boundaryImage.id);
        logSiteCardEvent({
          name: "Site Card Summary Tab - Site Image Downloaded",
          context: { source: "sitecard-lite" },
        });
      },
    });

    const onImgExportClick = () => {
      loadSiteImage();
    };

    useEffect(() => {
      if (!loading) {
        hideSkeletonLoading();
      }
    }, [loading]);

    const siteFeatures = useMemo(() => {
      const siteGeometry = data?.siteOverview.geometry;
      if (!siteGeometry) {
        return [];
      } else {
        return [turf.feature(siteGeometry)];
      }
    }, [data?.siteOverview.geometry]);

    return (
      <>
        {!loading && (
          <Map
            mapZoom={mapZoom}
            bounds={bounds}
            siteLatLng={siteLatLng}
            siteFeatures={siteFeatures}
            siteColor={data?.siteOverview._stage.color}
            interactive={interactive}
            initialMapTypeId={initialMapTypeId}
            onMapLoaded={onMapLoaded}
            layers={layers}
          />
        )}
        {interactive && (
          <div
            data-testid="map-buttons-overlay"
            className="atlas-flex atlas-absolute atlas-top-3 atlas-right-3"
          >
            {siteSelectionRouterLocation && (
              <Link
                role="button"
                onClick={logViewOnMapClick}
                to={siteSelectionRouterLocation}
                AnchorComponent={ButtonSecondaryAnchor}
              >
                <i className="icon-lt-map-2-line atlas-mr-1 atlas-h-3.5 atlas-w-3.5" />
                {t("sites.card.map.view_site_on_map")}
              </Link>
            )}
            {showExportImageButton && (
              <Button.Secondary
                onClick={onImgExportClick}
                className="atlas-pl-2.5 atlas-pr-2 atlas-pb-2 atlas-ml-2.5"
                disabled={loadingSiteImage}
              >
                <i className="icon-lt-download-2-line atlas-h-4 atlas-w-4 atlas-mt-px atlas-m-px" />
              </Button.Secondary>
            )}
          </div>
        )}
      </>
    );
  },
  {
    showPadding: false,
  }
);

export const ButtonSecondaryAnchor: FC<AnchorHTMLAttributes<HTMLAnchorElement>> = ({
  children,
  ...props
}) => (
  <Button.Secondary buttonType="Anchor" {...props}>
    {children}
  </Button.Secondary>
);
